// eslint-disable-next-line @nx/enforce-module-boundaries
import { type WithWebviewPageConfigProps } from 'shared/page-enhancers'
import { z } from 'zod'

export enum SurveysQuestionTypeEnum {
  YesNo = 'YesNo',
  MultipleChoiceSingle = 'MultipleChoiceSingle', // radio buttons
  MultipleChoice = 'MultipleChoice', // checkbox buttons
  Score = 'Score',
  FreeText = 'FreeText',
  Unavailable = 'Unavailable'
}

export const ZSurveyQuestionId = z.string().superRefine((id, ctx) => {
  if (id.includes(' ')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Question id not allowed, spaces are not allowed.'
    })
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(id)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Question id not allowed, use only alphanumeric characters, hyphens, or underscores.'
    })
  }
})

export const ZSurveyQuestionBase = z.object({
  id: ZSurveyQuestionId,
  type: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  buttonLabel: z.string(),
  scale: z.enum(['number', 'smiley']).optional(),
  options: z
    .array(
      z.object({
        id: z.string().optional().nullable(),
        text: z.string().optional().nullable(),
        isCustomTextOption: z.boolean().optional().nullable()
      })
    )
    .optional()
    .nullable(),
  required: z.boolean()
})

export const ZSurveyScoreQuestion = ZSurveyQuestionBase.extend({
  type: z.literal(SurveysQuestionTypeEnum.Score),
  lowerLabel: z.string().optional(),
  upperLabel: z.string().optional()
})

export const ZSurveyMultipleChoiceQuestion = ZSurveyQuestionBase.extend({
  type: z.literal(SurveysQuestionTypeEnum.MultipleChoice)
})

export const ZSurveyMultipleChoiceSingleQuestion = ZSurveyQuestionBase.extend({
  type: z.literal(SurveysQuestionTypeEnum.MultipleChoiceSingle),
  allowCustomText: z.boolean().optional(),
  showAdditionalComment: z.boolean().optional()
})

export const ZSurveyYesNoQuestion = ZSurveyQuestionBase.extend({
  type: z.literal(SurveysQuestionTypeEnum.YesNo),
  yesLabel: z.string().optional(),
  noLabel: z.string().optional(),
  yesOptionId: z.string().optional(),
  noOptionId: z.string().optional()
})

export const ZSurveyFreeTextQuestion = ZSurveyQuestionBase.extend({
  type: z.literal(SurveysQuestionTypeEnum.FreeText),
  inputType: z.enum(['input', 'textarea']).optional().default('input'),
  maxLength: z.number().optional(),
  placeholder: z.string().optional()
})

export type SurveyQuestionId = z.infer<typeof ZSurveyQuestionId>
export type SurveyScoreQuestion = z.infer<typeof ZSurveyScoreQuestion>
export type SurveyMultipleChoiceQuestion = z.infer<typeof ZSurveyMultipleChoiceQuestion>
export type SurveyMultipleChoiceSingleQuestion = z.infer<typeof ZSurveyMultipleChoiceSingleQuestion>
export type SurveyYesNoQuestion = z.infer<typeof ZSurveyYesNoQuestion>
export type SurveyFreeTextQuestion = z.infer<typeof ZSurveyFreeTextQuestion>

export type TExpectedWithWebviewPageConfigProps = WithWebviewPageConfigProps<
  'stone_account' | 'banking' | 'stone_account_kyc'
>

export type TWebviewRuntime = NonNullable<TExpectedWithWebviewPageConfigProps['webviewRuntime']>
